import * as React from "react";
import { Page } from "@ludens-reklame/rubics-v3-sdk/page/types";
import { useTagFilteredSearch } from "../../hooks/useTagFilteredSearch.js";
import { transformImageUrl } from "@ludens-reklame/rubics-v3-sdk/file/utils";
import { useStore } from "@ludens-reklame/rubics-v3-react/store/hooks";
import {
  Constrainer,
  HorizontalSpacer,
  VerticalSpacer,
} from "../../internal-components/Layout.js";
import { Busy } from "../../internal-components/Busy.js";
import { RichText } from "../../internal-components/RichText.js";
import { Button } from "../../internal-components/Button.js";
import { PageFilter } from "../../internal-components/page-filter/PageFilter.js";
import { StoreBuckets, Department } from "../../types/dataBuckets.js";
import { chevron } from "../../icons/index.js";

import {
  JobListingsList,
  LoadMore,
  JobCard,
  JobCardContent,
  JobCardTitle,
  JobCardMeta,
  JobCardMetaItem,
  JobCardMetaLabel,
  JobCardMetaValue,
  JobCardImage,
} from "./styles.js";

export interface JobPageProps {
  location?: string;
  department?: Department;
  deadline?: string;
}

export type JobListingItem = Page<JobPageProps>;

export interface CustomFilterGroup {
  label?: string;
  tags?: { value?: string }[];
}

export interface JobListingsProps {
  limit?: number;
  showMoreText?: string;
  list?: JobListingItem[];
  parent?: Page;
  withSearchAndFilter?: boolean;
  filterSearchLabel?: string;
  filterSearchPlaceholder?: string;
  showAllText?: string;
  resultCountText?: string;
  filterVariant?: "stillinginfo" | "egendefinert";
  filterGroups?: CustomFilterGroup[];
  requiredTags?: { value?: string }[];
}

export const JobListings: React.FC<JobListingsProps> = ({
  limit = 10,
  showMoreText,
  list = [],
  parent,
  withSearchAndFilter,
  filterSearchLabel,
  filterSearchPlaceholder,
  showAllText,
  resultCountText,
  filterVariant = "stillinginfo",
  filterGroups: customFilterGroups = [],
  requiredTags = [],
}) => {
  const store = useStore<{}, StoreBuckets>();

  const parentId = parent ? parent._id : store.context.page?._id;

  const resolvedRequiredTags = requiredTags
    .map((tag) => tag.value)
    .filter((value): value is string => !!value);

  const {
    results,
    unfilteredResults,
    unfilteredTotal,
    loading,
    hasFetched,
    hasMore,
    loadMore,
    onFilterChange,
  } = useTagFilteredSearch<JobPageProps>({
    baseQuery: {
      childOf: parentId ? [parentId] : [],
      "sort.publishedDate": -1,
    },
    requiredTags: resolvedRequiredTags,
    pageSize: limit,
    fetchOnInit: !!parentId,
    matchesFilters:
      filterVariant === "egendefinert" ? undefined : matchesJobFilters,
  });

  const allItems = unfilteredResults ?? list;
  const items = hasFetched ? results : list;

  const resolvedFilterGroups =
    filterVariant === "egendefinert"
      ? customFilterGroups
          .filter((g) => g.label && g.tags?.length)
          .map((g) => ({
            label: g.label!,
            key: g.label!,
            options: (g.tags || [])
              .filter((t) => t.value)
              .map((t) => ({ label: t.value!, value: t.value! })),
          }))
      : [
          {
            label: "Sted",
            key: "location",
            options: Array.from(
              new Set(
                allItems
                  .map((i) => i.props?.location)
                  .filter(Boolean) as string[]
              )
            ).map((loc) => ({ label: loc, value: loc })),
          },
          {
            label: "Selskap",
            key: "department",
            options: (store.context.dataBuckets?.departments || []).map(
              (d) => ({
                label: d.name || "",
                value: d.name || "",
              })
            ),
          },
        ];

  return (
    <VerticalSpacer>
      <Constrainer>
        <HorizontalSpacer>
          {withSearchAndFilter && (
            <PageFilter
              filterGroups={resolvedFilterGroups}
              searchLabel={filterSearchLabel || undefined}
              searchPlaceholder={filterSearchPlaceholder || undefined}
              showAllText={showAllText || undefined}
              resultCountText={resultCountText || undefined}
              onFilterChange={onFilterChange}
              resultCount={
                unfilteredTotal !== undefined
                  ? { shown: items.length, total: unfilteredTotal }
                  : undefined
              }
            />
          )}
          <Busy $busy={loading}>
            <JobListingsList>
              {items.map((item) => (
                <ListingCard key={item._id} {...item} />
              ))}
            </JobListingsList>
            {hasMore && (
              <LoadMore>
                <Button onClick={loadMore}>
                  {showMoreText || "Vis flere"}
                </Button>
              </LoadMore>
            )}
          </Busy>
        </HorizontalSpacer>
      </Constrainer>
    </VerticalSpacer>
  );
};

function matchesJobFilters(
  item: JobListingItem,
  { location = [], department = [] }: Record<string, string[]>
) {
  return (
    (!location.length || location.includes(item.props?.location || "")) &&
    (!department.length ||
      department.includes(item.props?.department?.name || ""))
  );
}

function formatDeadline(dateStr: string) {
  return dateStr;
}

function MetaItem({ label, value }: { label: string; value: string }) {
  return (
    <JobCardMetaItem>
      {chevron}
      <div>
        <JobCardMetaLabel>{label}</JobCardMetaLabel>
        <JobCardMetaValue>{value}</JobCardMetaValue>
      </div>
    </JobCardMetaItem>
  );
}

function ListingCard({ title, excerpt, image, url, props }: JobListingItem) {
  return (
    <JobCard as={url ? "a" : "div"} href={url || undefined}>
      <JobCardContent>
        {title && <JobCardTitle>{title}</JobCardTitle>}
        {excerpt && <RichText dangerouslySetInnerHTML={{ __html: excerpt }} />}
        <JobCardMeta>
          {props?.location && <MetaItem label="Sted" value={props.location} />}
          {props?.department?.name && (
            <MetaItem label="Selskap" value={props.department.name} />
          )}
          {props?.deadline && (
            <MetaItem
              label="Søknadsfrist"
              value={formatDeadline(props.deadline)}
            />
          )}
        </JobCardMeta>
      </JobCardContent>
      {image && (
        <JobCardImage
          src={transformImageUrl(image, {
            width: 560,
            height: 300,
            fit: "cover",
          })}
          alt={image.altText || ""}
          width={560}
          height={300}
        />
      )}
    </JobCard>
  );
}
